import { Injectable, NgZone, ElementRef } from '@angular/core';
import { ScrollBarService } from '@shared/scroll/scrollbar.service';
import { NgScrollbar } from 'ngx-scrollbar';


export interface IntersectionTarget {
  elRef: ElementRef;
  updateVisibility?: (isVisible: boolean, ratio: number) => void;
}

@Injectable()
export class IntersectionObserverService {

  private observer: IntersectionObserver;
  private targets: Map<Element, IntersectionTarget>;

  // I initialize the lazy-viewport service.
  constructor(
    private zone: NgZone,
    private scrollBarService: ScrollBarService
  ) {

    this.observer = null;

    // The IntersectionObserver watches Elements. However, when an element visibility
    // changes, we have to alert an Angular Directive instance. As such, we're going
    // to keep a map of Elements-to-Directives. This way, when our observer callback
    // is invoked, we'll be able to extract the appropriate Directive from the
    // Element-based observer entries collection.
    this.targets = new Map();

  }

  // ---
  // PUBLIC METHODS.
  // ---

  // I add the given IntersectionTarget implementation to the collection of objects being
  // tracked by the IntersectionObserver.
  public addTarget(target: IntersectionTarget): void {

    if (this.observer) {
      const el = target.elRef.nativeElement;
      if (el instanceof Element === false) {
        throw new Error('Intersection Observer service: Invalid target element');
      }

      this.targets.set(el, target);
      this.observer.observe(el);

      // provvedere polyfill https://github.com/w3c/IntersectionObserver/tree/master/polyfill
      // If we don't actually have an observer (lacking browser support), then we're
      // going to punt on the feature for now and just immediately tell the target
      // that it is visible on the page.
    } else {

      target.updateVisibility(true, 1.0);

    }

  }


  // I setup the IntersectionObserver with the given element as the root.
  public setup(scrollbar: NgScrollbar | string = null, rootMargin?: string, threshold?: number): void {

    // While the IntersectionObserver is supported in the modern browsers, it will
    // never be added to Internet Explorer (IE) and is not in my version of Safari
    // (at the time of this post). As such, we'll only use it if it's available.
    // And, if it's not, we'll fall-back to non-lazy behaviors.
    let sb: NgScrollbar;
    if (!global['IntersectionObserver']) {

      return;

    }
    if (scrollbar) {
      sb = typeof scrollbar === 'string' ? this.scrollBarService.getById(scrollbar) : scrollbar;
    } else {
      sb = this.scrollBarService.mainScrollbar;
    }


    this.zone.runOutsideAngular(
      () => {
        this.observer = new IntersectionObserver(
          this.handleIntersectionUpdate,
          {
            root: sb.viewport.nativeElement,
            rootMargin: rootMargin ? rootMargin : '0px 0px 0px 0px',
            threshold: threshold ? threshold : 0
          }
        );
      });

  }


  // I remove the given LazyTarget implementation from the collection of objects being
  // tracked by the IntersectionObserver.
  public removeTarget(target: ElementRef): void {

    // If the IntersectionObserver isn't supported, we never started tracking the
    // given target in the first place.
    if (this.observer) {
      const el = target.nativeElement;
      this.targets.delete(el);
      this.observer.unobserve(el);
    }

  }


  // I teardown this service instance.
  public teardown(): void {

    if (this.observer) {

      this.observer.disconnect();
      this.observer = null;

    }

    this.targets.clear();
    this.targets = null;

  }

  // ---
  // PRIVATE METHODS.
  // ---

  // I handle changes in the visibility for elements being tracked by the intersection
  // observer.
  // --
  // CAUTION: Using fat-arrow binding for method.
  private handleIntersectionUpdate = (entries: IntersectionObserverEntry[]): void => {

    for (const entry of entries) {

      const intersectionTarget = this.targets.get(entry.target);

      if (intersectionTarget) {
        intersectionTarget.updateVisibility(
          entry.isIntersecting,
          entry.intersectionRatio
        );
      }
    }

  }

}
